import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { filter, firstValueFrom, map, tap } from 'rxjs';
import { FeedbackApiService } from '../api/feedback-api.service';
import { FeedbackAttachmentDraft } from '../types/feedback-attachment-draft';
import { FeedbackCreateRequest } from '../types/feedback-api.types';
import { environment } from '../../../../environments/environment';
import { extractFeedbackErrorMessage } from './feedback-error.util';
import { FeedbackDraftInput, FeedbackPayloadBuilderService } from './feedback-payload-builder.service';
import { FeedbackPayloadValidatorService } from './feedback-payload-validator.service';

export interface FeedbackSubmissionResult {
  valid: boolean;
  errors: readonly string[];
  feedbackPayload: FeedbackCreateRequest;
  backendMessage: string | null;
  createdFeedbackId: number | null;
  attachmentResults: readonly FeedbackAttachmentUploadResult[];
}

export interface FeedbackAttachmentUploadResult {
  draftId: string;
  fileName: string;
  status: 'pending' | 'uploading' | 'uploaded' | 'failed';
  error: string | null;
  isDiagnostics: boolean;
}

export interface FeedbackAttachmentProgressUpdate {
  draftIds: readonly string[];
  progress: number;
  phase: 'attachments' | 'diagnostics';
}

export type FeedbackSubmissionInput = FeedbackDraftInput;

@Injectable({
  providedIn: 'root',
})
export class FeedbackSubmissionService {
  private readonly payloadBuilder = inject(FeedbackPayloadBuilderService);
  private readonly payloadValidator = inject(FeedbackPayloadValidatorService);
  private readonly feedbackApi = inject(FeedbackApiService);
  private readonly translate = inject(TranslateService);

  async submit(
    input: FeedbackSubmissionInput,
    attachmentDrafts: readonly FeedbackAttachmentDraft[] = [],
    onUploadStart?: () => void,
    onUploadProgress?: (update: FeedbackAttachmentProgressUpdate) => void,
  ): Promise<FeedbackSubmissionResult> {
    const feedbackPayload = this.payloadBuilder.buildFeedbackPayload(input);

    const validation = this.payloadValidator.validateFeedbackPayload(feedbackPayload);

    if (!validation.valid) {
      return {
        valid: false,
        errors: validation.errors,
        feedbackPayload,
        backendMessage: null,
        createdFeedbackId: null,
        attachmentResults: this.buildAttachmentResults(attachmentDrafts),
      };
    }

    const submissionDrafts = [...attachmentDrafts];
    
    try {
      const createdFeedback = await firstValueFrom(this.feedbackApi.create(feedbackPayload));
      const createdFeedbackId = createdFeedback.id ?? null;

      if (createdFeedbackId === null) {
        throw new Error('Feedback was created but no identifier was returned.');
      }
      
      const userAttachmentDrafts = submissionDrafts.filter((draft) => !draft.isDiagnostics);
      const diagnosticsDrafts = submissionDrafts.filter((draft) => draft.isDiagnostics);
      const totalUploadCount = submissionDrafts.length;
      if (totalUploadCount > 0) {
        onUploadStart?.();
      }
      let completedUploadCount = 0;

      const userAttachmentResults = await this.uploadAttachmentBatch(
        createdFeedbackId,
        userAttachmentDrafts,
        'attachments',
        completedUploadCount,
        totalUploadCount,
        onUploadProgress,
      );
      completedUploadCount += userAttachmentDrafts.length;

      const diagnosticsResults = await this.uploadAttachmentBatch(
        createdFeedbackId,
        diagnosticsDrafts,
        'diagnostics',
        completedUploadCount,
        totalUploadCount,
        onUploadProgress,
      );

      const attachmentResults = [...userAttachmentResults, ...diagnosticsResults];
      const hasUserAttachmentFailures = attachmentResults.some(
        (result) => !result.isDiagnostics && result.status === 'failed',
      );
      const hasDiagnosticsFailures = attachmentResults.some((result) => result.isDiagnostics && result.status === 'failed');

      return {
        valid: true,
        errors:
          hasUserAttachmentFailures || hasDiagnosticsFailures
            ? [
                hasUserAttachmentFailures && hasDiagnosticsFailures
                  ? this.translate.instant('feedbackWidget.messages.attachmentsAndDiagnosticsUploadFailed')
                  : hasDiagnosticsFailures
                    ? this.translate.instant('feedbackWidget.messages.diagnosticsUploadFailed')
                    : this.translate.instant('feedbackWidget.messages.uploadFailedDetail'),
              ]
            : [],
        feedbackPayload,
        backendMessage: hasUserAttachmentFailures || hasDiagnosticsFailures
          ? this.translate.instant('feedbackWidget.messages.savedWithAttachmentIssuesTitle')
          : this.translate.instant('feedbackWidget.success.title'),
        createdFeedbackId,
        attachmentResults,
      };
    } catch (error) {
      const backendMessage = extractFeedbackErrorMessage(
        error,
        this.translate,
        this.translate.instant('feedbackWidget.messages.submissionFailedDetail'),
      );
      if (!environment.production) {
        console.error('FEEDBACK_SUBMIT_FAILED', {
          feedbackId: null,
          backendMessage,
          error,
        });
      }

      return {
        valid: false,
        errors: [backendMessage],
        feedbackPayload,
        backendMessage,
        createdFeedbackId: null,
        attachmentResults: this.buildAttachmentResults(attachmentDrafts),
      };
    }
  }

  private buildAttachmentResults(
    drafts: readonly FeedbackAttachmentDraft[],
  ): readonly FeedbackAttachmentUploadResult[] {
    return drafts.map((draft) => ({
      draftId: draft.id,
      fileName: draft.file.name,
      status: draft.uploadStatus,
      error: draft.uploadError,
      isDiagnostics: draft.isDiagnostics,
    }));
  }

  private async uploadAttachmentBatch(
    feedbackId: number,
    drafts: readonly FeedbackAttachmentDraft[],
    phase: 'attachments' | 'diagnostics',
    completedUploadCount: number,
    totalUploadCount: number,
    onUploadProgress?: (update: FeedbackAttachmentProgressUpdate) => void,
  ): Promise<readonly FeedbackAttachmentUploadResult[]> {
    if (!drafts.length) {
      return [];
    }

    try {
      const uploadedAttachments = await firstValueFrom(
        this.feedbackApi
          .addAttachmentsWithProgress(
            feedbackId,
            drafts.map((draft) => draft.file),
          )
          .pipe(
            tap((event) => {
              const overallProgress =
                totalUploadCount > 0
                  ? Math.round(
                      ((completedUploadCount + (drafts.length * event.progress) / 100) / totalUploadCount) * 100,
                    )
                  : event.progress;
              onUploadProgress?.({
                draftIds: drafts.map((draft) => draft.id),
                progress: overallProgress,
                phase,
              });
            }),
            filter((event) => event.kind === 'completed'),
            map((event) => event.attachments ?? []),
          ),
      );

      return drafts.map((draft, index) => {
        const uploadedAttachment = uploadedAttachments[index];

        return {
          draftId: draft.id,
          fileName: draft.file.name,
          status: uploadedAttachment ? 'uploaded' : 'failed',
          error: uploadedAttachment ? null : this.translate.instant('feedbackWidget.messages.attachmentUploadIncomplete'),
          isDiagnostics: draft.isDiagnostics,
        };
      });
    } catch (error) {
      const message = extractFeedbackErrorMessage(
        error,
        this.translate,
        this.translate.instant('feedbackWidget.messages.attachmentUploadFailed'),
      );

      return drafts.map((draft) => ({
        draftId: draft.id,
        fileName: draft.file.name,
        status: 'failed',
        error: message,
        isDiagnostics: draft.isDiagnostics,
      }));
    }
  }
}
